/**
 * お問合せ自動化: QA 管理画面（カテゴリ + QA 一覧）。
 * 仕様書: docs/01_spec/spec_inquiry_automation.md C-9.2
 */
import { ChevronDown, ChevronRight, Folder, FolderPlus, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import {
  type QACategoryRecord,
  type QAHistoryRecord,
  useAddManualQA,
  useArchiveQACategory,
  useDeleteQAItem,
  useQACategories,
  useQAByCategoryAndChannel,
  useUpdateQAItem,
  useUpsertQACategory,
} from '@/hooks/useInquiry';
import { formatDateTime } from '@/lib/firestoreTime';

import { InquiryEmbedActions } from './InquiryEmbedActions';

function CategoryDialog({
  category,
  open,
  onOpenChange,
}: {
  category: QACategoryRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const upsert = useUpsertQACategory();
  const [name, setName] = useState(category?.name ?? '');
  const [slug, setSlug] = useState(category?.slug ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [aiAutoEnabled, setAiAutoEnabled] = useState(category?.aiAutoEnabled ?? false);
  const [threshold, setThreshold] = useState(category?.confidenceThreshold ?? 0.85);

  // category prop が変更された場合に状態をリセット（init はそのままなので useEffect 不要、open 切替時のキー設定で対応想定）

  const handleSave = async () => {
    if (!name.trim() || !slug.trim()) {
      toast.error('名前とスラッグは必須です');
      return;
    }
    try {
      await upsert.mutateAsync({
        id: category?.id,
        name: name.trim(),
        slug: slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        description: description.trim(),
        aiAutoEnabled,
        confidenceThreshold: threshold,
        escalationKeywords: [],
      });
      toast.success(category ? '更新しました' : '作成しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{category ? 'カテゴリ編集' : '新規カテゴリ作成'}</DialogTitle>
          <DialogDescription>
            QA のグルーピングとAI 自動応答の閾値を設定します。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>カテゴリ名</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="イベント関連" />
          </div>
          <div>
            <Label>スラッグ（英数）</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="events-related"
            />
          </div>
          <div>
            <Label>説明（AI カテゴリ判定のヒント）</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="イベントの参加方法、料金、開催日時などに関する質問"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>AI 自動応答</Label>
            <Switch
              checked={aiAutoEnabled}
              onChange={(e) => setAiAutoEnabled(e.target.checked)}
            />
          </div>
          <div>
            <Label>信頼度閾値（{(threshold * 100).toFixed(0)}%）</Label>
            <input
              type="range"
              min={0.5}
              max={1}
              step={0.05}
              value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {category ? '更新' : '作成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QAEditDialog({
  qa,
  categories,
  open,
  onOpenChange,
}: {
  qa: QAHistoryRecord | null;
  categories: QACategoryRecord[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateQAItem();
  const [question, setQuestion] = useState(qa?.question ?? '');
  const [corrected, setCorrected] = useState(qa?.adminCorrectedAnswer ?? qa?.answer ?? '');
  const [categoryId, setCategoryId] = useState<string | ''>(qa?.categoryId ?? '');

  if (!qa) return null;

  const questionChanged = question !== qa.question;

  const handleApprove = async () => {
    try {
      if (!question.trim()) {
        toast.error('質問が空です');
        return;
      }
      if (!corrected.trim()) {
        toast.error('回答が空です');
        return;
      }
      const res = await update.mutateAsync({
        id: qa.id,
        question: questionChanged ? question : undefined,
        adminCorrectedAnswer: corrected !== qa.answer ? corrected : null,
        categoryId: categoryId === '' ? null : categoryId,
        adminApproved: true,
      });
      toast.success(
        res.embeddingRegenerated
          ? '保存しました（質問変更につき embedding 再生成済）'
          : '保存して承認しました',
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>QA 編集</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="!text-xs">質問（編集可）</Label>
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={3}
              className="text-[13px]"
            />
            {questionChanged && (
              <p className="text-[11px] text-[var(--text-muted)] mt-1">
                💡 質問変更時は保存後に embedding が再生成されます（ベクトル検索のため）。
              </p>
            )}
          </div>
          <div>
            <Label className="!text-xs">回答（編集可）</Label>
            <Textarea
              value={corrected}
              onChange={(e) => setCorrected(e.target.value)}
              rows={8}
              className="text-[13px]"
            />
          </div>
          <div>
            <Label className="!text-xs">カテゴリ</Label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--bg-elevated)] border border-[var(--border)] rounded text-[13px]"
            >
              <option value="">（未分類）</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            閉じる
          </Button>
          <Button onClick={handleApprove} disabled={update.isPending}>
            保存して承認
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QAAddDialog({
  defaultCategoryId,
  categories,
  open,
  onOpenChange,
}: {
  defaultCategoryId: string | null;
  categories: QACategoryRecord[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const add = useAddManualQA();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [categoryId, setCategoryId] = useState<string | ''>(defaultCategoryId ?? '');
  const [approved, setApproved] = useState(true);

  const handleCreate = async () => {
    if (!question.trim()) {
      toast.error('質問が空です');
      return;
    }
    if (!answer.trim()) {
      toast.error('回答が空です');
      return;
    }
    try {
      await add.mutateAsync({
        question: question.trim(),
        answer: answer.trim(),
        categoryId: categoryId === '' ? null : categoryId,
        adminApproved: approved,
      });
      toast.success('QA を追加しました');
      setQuestion('');
      setAnswer('');
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>QA を新規追加</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="!text-xs">質問</Label>
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={3}
              className="text-[13px]"
              placeholder="想定問答の質問本文..."
            />
          </div>
          <div>
            <Label className="!text-xs">回答</Label>
            <Textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={8}
              className="text-[13px]"
              placeholder="AI に学習させたい回答本文..."
            />
          </div>
          <div>
            <Label className="!text-xs">カテゴリ</Label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--bg-elevated)] border border-[var(--border)] rounded text-[13px]"
            >
              <option value="">（未分類）</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-between py-1">
            <div>
              <Label>承認済みフラグ</Label>
              <p className="text-[11px] text-[var(--text-muted)]">
                ON: AI 応答時の信頼度ブーストの対象になります（推奨）
              </p>
            </div>
            <Switch checked={approved} onChange={(e) => setApproved(e.target.checked)} />
          </div>
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-xs leading-relaxed text-[var(--text-secondary)]">
            ℹ️ 保存時に質問本文から embedding が自動生成され、ベクトル類似検索の対象になります。
            channel は <code>public</code> 固定です。
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            閉じる
          </Button>
          <Button onClick={handleCreate} disabled={add.isPending}>
            {add.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QACard({ qa, onEdit }: { qa: QAHistoryRecord; onEdit: (qa: QAHistoryRecord) => void }) {
  const [expanded, setExpanded] = useState(false);
  const deleteQA = useDeleteQAItem();

  const handleDelete = async () => {
    if (!confirm('この QA を削除します。AI の学習データ（ベクトル検索対象）からも即時に外れます。元に戻せません。')) {
      return;
    }
    try {
      await deleteQA.mutateAsync(qa.id);
      toast.success('QA を削除しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-3">
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-2 flex-1 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          <span className="text-[13px] line-clamp-1">{qa.question}</span>
        </button>
        <div className="flex items-center gap-1">
          {qa.adminApproved && <Badge variant="approved">承認済</Badge>}
          {qa.reviewStats && qa.reviewStats.count > 0 && (
            <Badge variant="default">
              <Star className="size-3" />
              {qa.reviewStats.average?.toFixed(1) ?? '-'} ({qa.reviewStats.count})
            </Badge>
          )}
          <span className="text-xs text-[var(--text-muted)] ml-2">
            {formatDateTime(qa.createdAt)}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="!text-xs">Q</Label>
            <div className="text-[13px] p-3 bg-[var(--bg-elevated)] rounded-sm whitespace-pre-wrap">
              {qa.question}
            </div>
          </div>
          <div>
            <Label className="!text-xs">A</Label>
            <div className="text-[13px] p-3 bg-[var(--bg-elevated)] rounded-sm whitespace-pre-wrap">
              {qa.adminCorrectedAnswer ?? qa.answer}
            </div>
          </div>
          <div className="col-span-full flex items-center justify-between">
            <Button size="sm" onClick={() => onEdit(qa)}>
              編集
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              disabled={deleteQA.isPending}
            >
              <Trash2 className="size-3.5" />
              {deleteQA.isPending ? '削除中...' : '削除'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function QAManagementPage() {
  const { data: categories, isLoading } = useQACategories();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<QACategoryRecord | null>(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingQA, setEditingQA] = useState<QAHistoryRecord | null>(null);
  const [qaDialogOpen, setQADialogOpen] = useState(false);
  const [qaAddDialogOpen, setQAAddDialogOpen] = useState(false);
  const archive = useArchiveQACategory();

  // 未分類含む選択肢
  const showUnclassified = selectedCategoryId === '__unclassified__';
  const qaQuery = useQAByCategoryAndChannel({
    categoryId: showUnclassified ? null : selectedCategoryId,
    // public / both のみ取得（LINE Bot の QA は LINE Bot > QAレビュー で管理）
    // 既存複合インデックス qa_history.categoryId + channel + createdAt を活用
    channels: ['public', 'both'],
  });

  if (isLoading) {
    return (
      <Screen title="QA 管理" actions={<InquiryEmbedActions />}>
        <div className="py-8 flex justify-center"><Spinner /></div>
      </Screen>
    );
  }

  return (
    <Screen
      title="QA 管理"
      description="質問と回答のカテゴリ管理 + AI 学習データの編集"
      actions={
        <>
          <InquiryEmbedActions />
          <Button
            onClick={() => {
              setEditingCategory(null);
              setCategoryDialogOpen(true);
            }}
          >
            <FolderPlus className="size-4" /> 新規カテゴリ
          </Button>
        </>
      }
    >
      {!selectedCategoryId ? (
        // カテゴリ一覧
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button
            className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4 text-left hover:border-[var(--accent)]"
            onClick={() => setSelectedCategoryId('__unclassified__')}
          >
            <div className="flex items-center gap-2 mb-2">
              <Folder className="size-5 text-[var(--text-muted)]" />
              <span className="font-semibold">未分類</span>
            </div>
            <div className="text-xs text-[var(--text-muted)]">
              カテゴライズされていない QA
            </div>
          </button>

          {(categories ?? []).map((cat) => (
            <button
              key={cat.id}
              className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4 text-left hover:border-[var(--accent)]"
              onClick={() => setSelectedCategoryId(cat.id)}
            >
              <div className="flex items-center gap-2 mb-2">
                <Folder className="size-5" />
                <span className="font-semibold">{cat.name}</span>
                {cat.aiAutoEnabled ? (
                  <Badge variant="approved">AI: ON</Badge>
                ) : (
                  <Badge variant="default">AI: OFF</Badge>
                )}
              </div>
              <div className="text-xs text-[var(--text-muted)] mb-2 line-clamp-2">
                {cat.description || 'no description'}
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span>{cat.qaCount} QA</span>
                {cat.averageRating != null && (
                  <span className="flex items-center gap-1">
                    <Star className="size-3" /> {cat.averageRating.toFixed(1)}
                  </span>
                )}
                <span className="text-[var(--text-muted)]">
                  閾値 {(cat.confidenceThreshold * 100).toFixed(0)}%
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        // QA 一覧（カテゴリ詳細）
        <div>
          <div className="flex items-center justify-between mb-3">
            <Button variant="secondary" onClick={() => setSelectedCategoryId(null)}>
              ← カテゴリ一覧へ戻る
            </Button>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setQAAddDialogOpen(true)}>
                <Plus className="size-3.5" /> QA を追加
              </Button>
              {selectedCategoryId !== '__unclassified__' && (
                <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const cat = categories?.find((c) => c.id === selectedCategoryId);
                    if (cat) {
                      setEditingCategory(cat);
                      setCategoryDialogOpen(true);
                    }
                  }}
                >
                  カテゴリ編集
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    if (!selectedCategoryId) return;
                    if (!confirm('このカテゴリをアーカイブします。紐付く QA は未分類に戻ります。')) return;
                    try {
                      await archive.mutateAsync(selectedCategoryId);
                      toast.success('アーカイブしました');
                      setSelectedCategoryId(null);
                    } catch (err) {
                      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
                    }
                  }}
                >
                  アーカイブ
                </Button>
                </>
              )}
            </div>
          </div>

          {qaQuery.isLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : !qaQuery.data || qaQuery.data.length === 0 ? (
            <EmptyState
              icon={Folder}
              title="QA がありません"
              description="このカテゴリに紐付く QA はまだありません。"
            />
          ) : (
            <div className="space-y-2">
              {qaQuery.data.map((qa) => (
                <QACard
                  key={qa.id}
                  qa={qa}
                  onEdit={(q) => {
                    setEditingQA(q);
                    setQADialogOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <CategoryDialog
        key={editingCategory?.id ?? 'new'}
        category={editingCategory}
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
      />
      <QAEditDialog
        key={editingQA?.id ?? 'none'}
        qa={editingQA}
        categories={categories ?? []}
        open={qaDialogOpen}
        onOpenChange={setQADialogOpen}
      />
      <QAAddDialog
        key={selectedCategoryId === '__unclassified__' ? 'unc' : (selectedCategoryId ?? 'none')}
        defaultCategoryId={
          selectedCategoryId === '__unclassified__' ? null : selectedCategoryId
        }
        categories={categories ?? []}
        open={qaAddDialogOpen}
        onOpenChange={setQAAddDialogOpen}
      />
    </Screen>
  );
}
